import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { CropsDataEnvelope } from "../../../models/crops";

import { useCropsQuery } from "./crops.query";
import {
  useCropsGlobalActivity,
  useCropsGlobalStatus,
} from "./crops.globalStatus";

export { useCropsGlobalActivity, useCropsGlobalStatus };

export * from "./crops.invalidate";

export const cropsAccessError = new Error(
  "Attempted to access crops data but it is not available",
);

// ####################
// ## useCropsStatus ##
// ####################

export type ResultOfUseCropsStatus = QueryStatus;
export type UseCropsStatus = () => ResultOfUseCropsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the crops data.
 */
export const useCropsStatus: UseCropsStatus = () => {
  return useCropsQuery({ watch: ["status"] }).status;
};

// ###################
// ## useCropsError ##
// ###################

export type ResultOfUseCropsError = AxiosError | null;
export type UseCropsError = () => ResultOfUseCropsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the crops data
 * or null if no error occurred.
 */
export const useCropsError: UseCropsError = () => {
  return useCropsQuery({ watch: ["error"] }).error;
};

// #######################
// ## useMaybeCropsData ##
// #######################

export type ResultOfUseMaybeCropsData = CropsDataEnvelope | undefined;
export type UseMaybeCropsData = () => ResultOfUseMaybeCropsData;

/**
 * The returned value of this hook will contain
 * the crops data, which may be undefined
 */
export const useMaybeCropsData: UseMaybeCropsData = () => {
  return useCropsQuery({ watch: ["data"] }).data;
};

// ##################
// ## useCropsData ##
// ##################

export type ResultOfUseCropsData = CropsDataEnvelope;
export type UseCropsData = () => ResultOfUseCropsData;

/**
 * The returned value of this hook will contain the crops data.
 *
 * This hook is guaranteed to contain crops data or will throw.
 */
export const useCropsData: UseCropsData = () => {
  const data = useMaybeCropsData();
  if (data === undefined) {
    throw cropsAccessError;
  }
  return data;
};

// ###################
// ## useMaybeCrops ##
// ###################

export type ParamsOfUseMaybeCrops = { forceDisable?: boolean };
export type ResultOfUseMaybeCrops = QueryEnvelope<CropsDataEnvelope>;
export type UseMaybeCrops = (
  params?: ParamsOfUseMaybeCrops,
) => ResultOfUseMaybeCrops;

/**
 * The `data` property returned from this hook will contain
 * the crops data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the crops data.
 */
export const useMaybeCrops: UseMaybeCrops = ({ forceDisable } = {}) => {
  const { status, error, data } = useCropsQuery({
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ##############
// ## useCrops ##
// ##############

export type ResultOfUseCrops = EnsuredQueryEnvelope<CropsDataEnvelope>;
export type UseCrops = () => ResultOfUseCrops;

/**
 * The `data` property returned from this hook will contain
 * the crops data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the crops data.
 *
 * This hook is guaranteed to contain crops data or will throw.
 */
export const useCrops: UseCrops = () => {
  const { status, error, data } = useCropsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw cropsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
